import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api, formatDateTime } from '../../utils/api'
import '../../styles/pages.css'

function DimensionTypeDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  const [dimensionType, setDimensionType] = useState(null)

  // Form state
  const [formName, setFormName] = useState('')
  const [isDirty, setIsDirty] = useState(false)

  // Add value state
  const [newValue, setNewValue] = useState('')
  const [addingValue, setAddingValue] = useState(false)

  // Edit value state
  const [editingValueId, setEditingValueId] = useState(null)
  const [editingValueText, setEditingValueText] = useState('')
  const [savingValue, setSavingValue] = useState(false)

  // Delete confirm state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingValueId, setDeletingValueId] = useState(null)

  useEffect(() => {
    loadData()
  }, [id])

  async function loadData() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/pim/dimension-types/${id}`)
      setDimensionType(response)
      setFormName(response.name || '')
      setIsDirty(false)
    } catch (err) {
      console.error('Failed to load dimension type:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function handleNameChange(value) {
    setFormName(value)
    setIsDirty(value !== dimensionType?.name)
    setSuccess(null)
  }

  async function handleSaveName(e) {
    e.preventDefault()
    if (!formName.trim()) {
      setError('Name is required')
      return
    }

    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await api.patch(`/api/pim/dimension-types/${id}`, { name: formName.trim() })
      setDimensionType(response)
      setFormName(response.name)
      setIsDirty(false)
      setSuccess('Dimension type saved successfully')
    } catch (err) {
      console.error('Failed to save dimension type:', err)
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleAddValue(e) {
    e.preventDefault()
    if (!newValue.trim()) {
      setError('Value is required')
      return
    }

    setAddingValue(true)
    setError(null)
    setSuccess(null)

    try {
      await api.post(`/api/pim/dimension-types/${id}/values`, { value: newValue.trim() })
      setNewValue('')
      setSuccess('Value added successfully')
      await loadData()
    } catch (err) {
      console.error('Failed to add value:', err)
      setError(err.message)
    } finally {
      setAddingValue(false)
    }
  }

  function startEditingValue(value) {
    setEditingValueId(value.dimension_type_value_id)
    setEditingValueText(value.value)
    setError(null)
    setSuccess(null)
  }

  function cancelEditingValue() {
    setEditingValueId(null)
    setEditingValueText('')
  }

  async function handleSaveValue(valueId) {
    if (!editingValueText.trim()) {
      setError('Value is required')
      return
    }

    setSavingValue(true)
    setError(null)
    setSuccess(null)

    try {
      await api.patch(`/api/pim/dimension-types/${id}/values/${valueId}`, { value: editingValueText.trim() })
      setEditingValueId(null)
      setEditingValueText('')
      setSuccess('Value updated successfully')
      await loadData()
    } catch (err) {
      console.error('Failed to update value:', err)
      setError(err.message)
    } finally {
      setSavingValue(false)
    }
  }

  async function handleDeleteValue(valueId) {
    setDeletingValueId(valueId)
    setError(null)
    setSuccess(null)

    try {
      await api.delete(`/api/pim/dimension-types/${id}/values/${valueId}`)
      setSuccess('Value deleted successfully')
      await loadData()
    } catch (err) {
      console.error('Failed to delete value:', err)
      // Parse error for usage details
      const errorData = err.response?.data?.detail
      if (errorData && typeof errorData === 'object') {
        setError(`${errorData.message} See variants using this value below.`)
      } else {
        setError(err.message)
      }
    } finally {
      setDeletingValueId(null)
    }
  }

  async function handleDelete() {
    setDeleting(true)
    setError(null)

    try {
      await api.delete(`/api/pim/dimension-types/${id}`)
      navigate('/pim/dimensions')
    } catch (err) {
      console.error('Failed to delete dimension type:', err)
      // Parse error for usage details
      const errorData = err.response?.data?.detail
      if (errorData && typeof errorData === 'object') {
        setError(`${errorData.message}`)
      } else {
        setError(err.message)
      }
      setShowDeleteConfirm(false)
    } finally {
      setDeleting(false)
    }
  }

  function handleProductClick(productId) {
    navigate(`/pim/products/${productId}`)
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!dimensionType) {
    return (
      <div className="card">
        <div className="empty-state">
          <div className="empty-state-title">Dimension Type Not Found</div>
          <div className="empty-state-description">The requested dimension type could not be found.</div>
          <button className="btn btn-primary" onClick={() => navigate('/pim/dimensions')}>
            Back to Dimension Types
          </button>
        </div>
      </div>
    )
  }

  const canDelete = dimensionType.total_usage_count === 0

  return (
    <>
      {/* Back Button */}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => navigate('/pim/dimensions')}
        style={{ marginBottom: 'var(--space-4)' }}
      >
        &larr; Back to Dimension Types
      </button>

      {error && (
        <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <strong>Error:</strong> {error}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setError(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="card" style={{ background: 'var(--color-success-light)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          {success}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setSuccess(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Edit Name Section */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ margin: '0 0 var(--space-4) 0' }}>Edit Dimension Type</h3>
        <form onSubmit={handleSaveName}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="filter-group" style={{ flex: 1, minWidth: 200 }}>
              <label className="filter-label">Name *</label>
              <input
                type="text"
                className="form-input"
                value={formName}
                onChange={e => handleNameChange(e.target.value)}
                placeholder="Enter dimension type name"
                required
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || !isDirty || !formName.trim()}
            >
              {saving ? 'Saving...' : 'Save Name'}
            </button>
          </div>
        </form>
        <div className="text-sm text-muted" style={{ marginTop: 'var(--space-3)' }}>
          Created: {formatDateTime(dimensionType.created_at)} |
          Updated: {formatDateTime(dimensionType.updated_at)} |
          Total Usage: {dimensionType.total_usage_count} variant(s)
        </div>
      </div>

      {/* Values Section */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <h3 style={{ margin: '0 0 var(--space-4) 0' }}>Values ({dimensionType.values.length})</h3>

        {/* Add Value Form */}
        <form onSubmit={handleAddValue} style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
            <div className="filter-group" style={{ flex: 1 }}>
              <input
                type="text"
                className="form-input"
                value={newValue}
                onChange={e => setNewValue(e.target.value)}
                placeholder="Add new value..."
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={addingValue || !newValue.trim()}
            >
              {addingValue ? 'Adding...' : '+ Add'}
            </button>
          </div>
        </form>

        {/* Values Table */}
        {dimensionType.values.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-description">
              No values yet. Add values above to define options for this dimension type.
            </div>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Value</th>
                <th style={{ textAlign: 'center' }}>Usage</th>
                <th style={{ width: 150, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {dimensionType.values.map(value => (
                <tr key={value.dimension_type_value_id}>
                  <td>
                    {editingValueId === value.dimension_type_value_id ? (
                      <input
                        type="text"
                        className="form-input"
                        value={editingValueText}
                        onChange={e => setEditingValueText(e.target.value)}
                        autoFocus
                        style={{ maxWidth: 300 }}
                      />
                    ) : (
                      <span style={{ fontWeight: 500 }}>{value.value}</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge ${value.usage_count > 0 ? 'badge-success' : 'badge-neutral'}`}>
                      {value.usage_count} variant{value.usage_count !== 1 ? 's' : ''}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {editingValueId === value.dimension_type_value_id ? (
                      <div style={{ display: 'flex', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSaveValue(value.dimension_type_value_id)}
                          disabled={savingValue || !editingValueText.trim()}
                        >
                          {savingValue ? '...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={cancelEditingValue}
                          disabled={savingValue}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 'var(--space-1)', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => startEditingValue(value)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteValue(value.dimension_type_value_id)}
                          disabled={value.usage_count > 0 || deletingValueId === value.dimension_type_value_id}
                          title={value.usage_count > 0 ? `Cannot delete: ${value.usage_count} variant(s) using this value` : 'Delete value'}
                        >
                          {deletingValueId === value.dimension_type_value_id ? '...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Products Using This Type */}
      {dimensionType.products_using.length > 0 && (
        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ margin: '0 0 var(--space-4) 0' }}>
            Products Using This Type ({dimensionType.products_using.length})
          </h3>
          <table className="table">
            <thead>
              <tr>
                <th>Brand</th>
                <th>Model</th>
                <th style={{ textAlign: 'center' }}>Variants</th>
              </tr>
            </thead>
            <tbody>
              {dimensionType.products_using.map(product => (
                <tr
                  key={product.master_product_id}
                  onClick={() => handleProductClick(product.master_product_id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>{product.brand_name}</td>
                  <td style={{ fontWeight: 500 }}>{product.model}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="badge badge-info">{product.variant_count}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Danger Zone */}
      <div className="card" style={{ borderColor: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
        <h3 style={{ margin: '0 0 var(--space-4) 0', color: 'var(--color-error)' }}>Danger Zone</h3>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <strong>Delete Dimension Type</strong>
            <div className="text-sm text-muted">
              {canDelete
                ? 'This action cannot be undone.'
                : `Cannot delete: ${dimensionType.total_usage_count} variant(s) are using this dimension type.`
              }
            </div>
          </div>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={!canDelete}
          >
            Delete Type
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Dimension Type</h3>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowDeleteConfirm(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete "<strong>{dimensionType.name}</strong>"?</p>
              <p className="text-muted">This action cannot be undone. All {dimensionType.values.length} value(s) will also be deleted.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal styles */}
      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: var(--color-bg);
          border-radius: var(--radius-lg);
          width: 100%;
          max-width: 400px;
          box-shadow: var(--shadow-lg);
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: var(--space-4);
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h3 {
          margin: 0;
        }
        .modal-body {
          padding: var(--space-4);
        }
        .modal-body p {
          margin: 0 0 var(--space-2) 0;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-2);
          padding: var(--space-4);
          border-top: 1px solid var(--color-border);
        }
        .btn-danger {
          background: var(--color-error);
          color: white;
        }
        .btn-danger:hover:not(:disabled) {
          background: #c53030;
        }
        .btn-danger:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </>
  )
}

export default DimensionTypeDetail
